import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Server,
  Type,
  Power,
  Send,
  Mail,
} from "lucide-react";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteTitle, setSiteTitle] = useState("");
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [smtp, setSmtp] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // SMTP test
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/settings");
        setSiteTitle(data.site_title || "");
        setMaintenance(!!data.maintenance_mode);
        setMaintenanceMsg(data.maintenance_message || "");
        setSmtp(data.smtp);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await adminApi.put("/admin/settings", {
        site_title: siteTitle,
        maintenance_mode: maintenance,
        maintenance_message: maintenanceMsg,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpTest = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await adminApi.post("/admin/smtp/test", { to: testEmail.trim() });
      setTestResult({ ok: true, message: data.message || "Test email sent!" });
    } catch (e) {
      setTestResult({ ok: false, message: formatApiError(e) });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
          Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage site-wide configuration and maintenance state.
        </p>
      </div>

      {saved && (
        <div
          className="px-4 py-2.5 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center gap-2"
          data-testid="settings-saved"
        >
          <CheckCircle2 className="w-4 h-4" />
          Settings saved successfully
        </div>
      )}
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-5">
        {/* General */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Type className="w-4 h-4 text-[#FF9933]" />
            <h2 className="text-sm font-semibold text-gray-900">General</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Site Title
              </label>
              <input
                type="text"
                value={siteTitle}
                onChange={(e) => setSiteTitle(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                data-testid="site-title-input"
                placeholder="Ujjain Maha Kumbh 2028"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Displayed as the site title and tagline on public pages.
              </p>
            </div>
          </div>
        </section>

        {/* Maintenance */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Power className="w-4 h-4 text-[#FF9933]" />
            <h2 className="text-sm font-semibold text-gray-900">Maintenance Mode</h2>
          </div>

          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={maintenance}
                onChange={(e) => setMaintenance(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF9933] cursor-pointer"
                data-testid="maintenance-toggle"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Enable maintenance mode</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Shows a maintenance banner to public visitors.
                </p>
              </div>
            </label>

            {maintenance && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Maintenance mode is enabled. Visitors will see a banner on the public site.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Maintenance Message
              </label>
              <textarea
                rows={2}
                value={maintenanceMsg}
                onChange={(e) => setMaintenanceMsg(e.target.value)}
                disabled={!maintenance}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none disabled:bg-gray-50 disabled:text-gray-400 resize-none"
                data-testid="maintenance-message-input"
              />
            </div>
          </div>
        </section>

        {/* SMTP (read-only) */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-[#FF9933]" />
              <h2 className="text-sm font-semibold text-gray-900">SMTP Configuration</h2>
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                smtp?.configured
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
              data-testid="smtp-status-badge"
            >
              {smtp?.configured ? "Configured" : "Not configured (fallback: DB log)"}
            </span>
          </div>
          <div className="text-xs text-gray-500 mb-3">
            Read-only view. Update these via <code className="bg-gray-100 px-1 py-0.5 rounded">backend/.env</code> and restart the backend.
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              ["SMTP Host", smtp?.host],
              ["SMTP Port", smtp?.port],
              ["SMTP User", smtp?.user],
              ["Sender Email", smtp?.sender_email],
              ["Sender Name", smtp?.sender_name],
            ].map(([label, value]) => (
              <div key={label} className="p-3 bg-gray-50 rounded-md">
                <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  {label}
                </dt>
                <dd className="text-gray-900 font-mono text-xs break-all">
                  {value || <span className="text-gray-400 italic">not set</span>}
                </dd>
              </div>
            ))}
          </dl>

          {/* SMTP Test */}
          {smtp?.configured && (
            <div className="mt-5 pt-5 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-[#FF9933]" />
                <h3 className="text-sm font-semibold text-gray-900">Send Test Email</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Verify your SMTP works by sending a test email to any address.
              </p>
              <div className="flex items-start gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your-test-email@example.com"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                  data-testid="smtp-test-email-input"
                />
                <button
                  type="button"
                  onClick={handleSmtpTest}
                  disabled={testing || !testEmail.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-medium transition-colors disabled:opacity-60"
                  data-testid="smtp-test-send-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {testing ? "Sending..." : "Send Test"}
                </button>
              </div>
              {testResult && (
                <div
                  className={`mt-3 p-3 rounded-md border text-xs ${
                    testResult.ok
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                  data-testid="smtp-test-result"
                >
                  <div className="flex items-start gap-2">
                    {testResult.ok ? (
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 break-words">
                      <p className="font-semibold">{testResult.ok ? "Success!" : "Failed"}</p>
                      <p className="mt-0.5">{testResult.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Save */}
        <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-[#f0f0f1] py-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-medium transition-colors disabled:opacity-60"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
